import { useState, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Target, Users, Search, X } from "lucide-react";
import { useNetworkMembers, NetworkMember } from "@/hooks/useNetworkMembers";

export interface SprintInvitees {
  userIds: string[];
  emails: string[];
}

interface CreateSprintFormProps {
  userId?: string;
  onSubmit: (objective: string, invitees: SprintInvitees) => void;
  onCancel: () => void;
  creating: boolean;
}

export const CreateSprintForm = ({ userId, onSubmit, onCancel, creating }: CreateSprintFormProps) => {
  const [objective, setObjective] = useState("");
  const [manualEmails, setManualEmails] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: networkMembers = [], isLoading: loadingNetwork } = useNetworkMembers(userId);

  const filteredMembers = useMemo(() => {
    if (!searchTerm) return networkMembers;
    const term = searchTerm.toLowerCase();
    return networkMembers.filter(
      member =>
        member.display_name?.toLowerCase().includes(term) ||
        member.username?.toLowerCase().includes(term) ||
        member.organization?.toLowerCase().includes(term)
    );
  }, [networkMembers, searchTerm]);

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    setSelectedUserIds(filteredMembers.map(m => m.id));
  };

  const handleDeselectAll = () => {
    setSelectedUserIds([]);
  };

  const parseEmails = (emailString: string): string[] => {
    return emailString
      .split(/[,\s]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes("@"));
  };

  const handleSubmit = () => {
    if (objective.trim() && objective.length >= 20) {
      const emails = parseEmails(manualEmails);
      onSubmit(objective.trim(), {
        userIds: selectedUserIds,
        emails
      });
    }
  };

  const selectedCount = selectedUserIds.length + parseEmails(manualEmails).length;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card className="p-8 max-w-3xl mx-auto">
      <div className="space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold">Launch a Sprint</h2>
          <p className="text-muted-foreground">
            Two simple steps to get your prompt sprint going
          </p>
        </div>

        <div className="space-y-6">
          {/* Step 1: Create Sprint Objective */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Create Sprint Objective</h3>
              </div>
            </div>
            <div className="ml-11">
              <Label htmlFor="objective">What do you want to achieve?</Label>
              <Textarea
                id="objective"
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                placeholder="Describe what you want to achieve with this prompt architecture sprint..."
                className="min-h-[120px] mt-2"
                maxLength={500}
              />
              <div className="text-xs text-muted-foreground mt-1 text-right">
                {objective.length}/500 characters
              </div>
              {objective.trim() && objective.length < 20 && (
                <p className="text-sm text-destructive mt-1">
                  Objective should be at least 20 characters
                </p>
              )}
            </div>
          </div>

          {/* Step 2: Invite Your Team */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Invite Your Team</h3>
                {selectedCount > 0 && (
                  <span className="text-sm text-muted-foreground">
                    ({selectedCount} selected)
                  </span>
                )}
              </div>
            </div>
            <div className="ml-11">
              <Tabs defaultValue="network" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="network">Your Network</TabsTrigger>
                  <TabsTrigger value="email">Invite by Email</TabsTrigger>
                </TabsList>

                <TabsContent value="network" className="mt-4 space-y-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search your network..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {/* Select/Deselect All */}
                  {filteredMembers.length > 0 && (
                    <div className="flex gap-2 text-sm">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                        className="h-7 px-2"
                      >
                        Select all
                      </Button>
                      {selectedUserIds.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleDeselectAll}
                          className="h-7 px-2"
                        >
                          Deselect all
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Network Members List */}
                  <ScrollArea className="h-[200px] border rounded-md">
                    {loadingNetwork ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : filteredMembers.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center p-4">
                        <Users className="h-8 w-8 text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {searchTerm
                            ? "No members match your search"
                            : "No network members yet. Use the Email tab to invite collaborators."}
                        </p>
                      </div>
                    ) : (
                      <div className="p-2 space-y-1">
                        {filteredMembers.map((member) => (
                          <label
                            key={member.id}
                            className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedUserIds.includes(member.id)}
                              onCheckedChange={() => handleToggleUser(member.id)}
                            />
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={member.avatar_url} />
                              <AvatarFallback className="text-xs">
                                {getInitials(member.display_name || "?")}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {member.display_name}
                              </p>
                              {(member.title || member.organization) && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {[member.title, member.organization].filter(Boolean).join(" • ")}
                                </p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </ScrollArea>

                  {/* Selected Members Preview */}
                  {selectedUserIds.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedUserIds.map(id => {
                        const member = networkMembers.find(m => m.id === id);
                        if (!member) return null;
                        return (
                          <div
                            key={id}
                            className="flex items-center gap-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs"
                          >
                            <span>{member.display_name}</span>
                            <button
                              type="button"
                              onClick={() => handleToggleUser(id)}
                              className="hover:bg-primary/20 rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="email" className="mt-4 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Know someone who should join but isn't on iSPAI yet? Invite them by email.
                  </p>
                  <Input
                    type="text"
                    value={manualEmails}
                    onChange={(e) => setManualEmails(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    Separate multiple emails with commas. They'll receive an invitation to join the sprint.
                  </p>

                  {/* Email Preview */}
                  {parseEmails(manualEmails).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {parseEmails(manualEmails).map(email => (
                        <div
                          key={email}
                          className="bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs"
                        >
                          {email}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <p className="text-xs text-muted-foreground mt-3">
                Optional — you can also invite team members after the sprint is created.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleSubmit}
            disabled={creating || !objective.trim() || objective.length < 20}
            size="lg"
            className="flex-1"
          >
            {creating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Sprint...
              </>
            ) : (
              "Create Sprint"
            )}
          </Button>
          <Button
            variant="outline"
            onClick={onCancel}
            size="lg"
            disabled={creating}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
};
